<template>
  <div>
    <v-card class="mb-4">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <span>{{ $t('velocityOverTime') }}</span>
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-information"
                size="small"
                class="ms-1"
              ></v-icon>
            </template>
            <span>{{ $t('velocityTooltip') }}</span>
          </v-tooltip>
        </div>
        <div class="d-flex align-center">
          <v-btn-toggle v-model="groupBy" variant="outlined" mandatory class="me-2">
            <v-btn value="day">{{ $t('day') }}</v-btn>
            <v-btn value="week">{{ $t('week') }}</v-btn>
            <v-btn value="month">{{ $t('month') }}</v-btn>
          </v-btn-toggle>
          <v-btn
            icon
            elevation="0"
            text
            @click="resetZoom"
            class="me-2"
            :disabled="!isZoomed"
          >
            <v-icon>mdi-refresh</v-icon>
            <v-tooltip activator="parent" location="bottom">
              <span>{{ $t('burnup.resetZoom') }}</span>
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="buildMode === 'webapp'"
            icon
            elevation="0"
            text
            @click="downloadChart"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text>
        <v-chart class="chart" ref="chartRef" :option="chartOption" @zr:click="handleChartClick" />
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, defineProps, shallowRef } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, BarChart } from "echarts/charts";
import { 
  TooltipComponent, 
  LegendComponent, 
  GridComponent, 
  DataZoomComponent,
  ToolboxComponent 
} from "echarts/components";
import VChart from "vue-echarts";
import { downloadChartAsPNG } from '@/utils/chartUtils';

use([
  CanvasRenderer, 
  LineChart,
  BarChart, 
  TooltipComponent, 
  LegendComponent, 
  GridComponent,
  DataZoomComponent,
  ToolboxComponent
]);

const props = defineProps({
  filterContext: {
    type: Object,
    required: true
  },
  dataLoaded: {
    type:Boolean,
    required:true
  }
});

const store = useStore();
const { t } = useI18n();
const conn = computed(() => store.getters.getDBConnection);

const buildMode = computed(() => store.state.buildMode);
const statusTemplatesObj = computed(() => store.getters.getStatusTemplatesObj);
const chartColor = computed(() => store.getters.chartFontColor);

const chartRef = ref(null);
const chartOption = shallowRef({});
const groupBy = ref('week');
const chartData = ref({
  timeData: [],
  seriesData: [],
  rawData: new Map(),
  totalData: []
});

// Zoom related state
const isZoomed = ref(false);
const zoomStart = ref(0);
const zoomEnd = ref(100);

// Tooltip cache and debounce timer
const tooltipCache = ref(new Map());
const tooltipDebounceTimer = ref(null);
const currentTooltipParams = ref(null);

// Generic query execution function
const executeAndProcessQuery = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray();
};

const executeAndProcessTooltipQuery = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray().map((row) => {
    const jsonRow = row.toJSON();
    return {
      ...jsonRow,
      count: Number(jsonRow.count),
      sort: Number(jsonRow.sort)
    };
  });
}

const generateActivityQuery = (groupBy) => {
  // Determine time unit and corresponding SQL functions/formats
  const timeUnit = groupBy === 'month' ? 'month' : (groupBy === 'week' ? 'week' : 'day');

  // SQL expression for truncating the date/time to the specified unit
  const dateTruncSql = `DATE_TRUNC('${timeUnit}', validFrom)`;

  // SQL expression for formatting the truncated date/time into a unique key
  const timeKeySql =
      timeUnit === 'month' ? `strftime(${dateTruncSql}, '%Y-%m')`
    : timeUnit === 'week' ? `strftime(${dateTruncSql}, '%Y-W%V')` // %V is ISO week number
    : `strftime(${dateTruncSql}, '%Y-%m-%d')`; // %Y-%m-%d is ISO date format

  // Let's first debug with a simpler query to make sure we're getting data
  return `
    WITH time_periods AS (
      SELECT DISTINCT
        ${dateTruncSql} AS period_start,
        ${timeKeySql} AS time_key
      FROM filteredStatusHistory 
      WHERE validFrom IS NOT NULL
    ),
    status_activity AS (
      -- First get a count by status ID
      SELECT
        tp.time_key,
        sht.statusId,
        COUNT(DISTINCT sht.subItemId) AS activity_count
      FROM time_periods tp
      JOIN filteredStatusHistory sht
        ON ${dateTruncSql.replace('validFrom', 'sht.validFrom')} = tp.period_start
      GROUP BY
        tp.time_key,
        sht.statusId
      HAVING COUNT(DISTINCT sht.subItemId) > 0
    ),
    status_with_color AS (
      -- Join with status templates to get colors
      -- Use LEFT JOIN to debug if some statuses aren't matching
      SELECT
        sa.time_key,
        sa.statusId,
        st.color,
        st.sort,
        sa.activity_count
      FROM status_activity sa
      LEFT JOIN status_templates st ON sa.statusId = st.id
    ),
    color_activity AS (
      -- Aggregate by color
      SELECT
        time_key,
        color,
        COALESCE(MIN(sort), 999) AS sort, -- Default sort value if NULL
        SUM(activity_count) AS count
      FROM status_with_color
      GROUP BY time_key, color
    ),
    all_time_periods AS (
      -- Get all time periods
      SELECT time_key
      FROM time_periods
    ),
    all_colors AS (
      -- Get all colors from templates
      SELECT DISTINCT
        color,
        COALESCE(MIN(sort), 999) AS sort
      FROM status_templates
      GROUP BY color
    ),
    all_periods_and_colors AS (
      -- Create a grid of all periods and colors
      SELECT
        tp.time_key,
        c.color,
        c.sort
      FROM all_time_periods tp
      CROSS JOIN all_colors c
    )
    -- Final result with zeros for missing combinations
    SELECT
      apc.time_key as time_key,
      apc.color as color,
      apc.sort as sort,
      COALESCE(ca.count, 0) AS count
    FROM all_periods_and_colors apc
    LEFT JOIN color_activity ca
      ON apc.time_key = ca.time_key AND apc.color = ca.color
    ORDER BY
      apc.time_key,
      apc.sort
  `;
};

// Query for detailed tooltip data
const generateTooltipQuery = (timePoint, groupBy) => {

  const timeUnit = groupBy === 'month' ? 'month' :
                  (groupBy === 'week' ? 'week' : 'day');

  // Calculate the specific SQL boundary timestamp for the given timePoint
  let boundaryTimestampSQL;

  try { // Add error handling for parsing
    if (timeUnit === 'month') {
      const [year, month] = timePoint.split('-');
      if (!year || !month || isNaN(parseInt(year, 10)) || isNaN(parseInt(month, 10))) {
        throw new Error(`Invalid month format: ${timePoint}`);
      }
      boundaryTimestampSQL = `DATE_TRUNC('month', MAKE_DATE(${parseInt(year, 10)}, ${parseInt(month, 10)}, 1))`;
    } else if (timeUnit === 'week') {
      const [year, weekStr] = timePoint.split('-W');
      const week = parseInt(weekStr, 10);
       if (!year || !weekStr || isNaN(parseInt(year, 10)) || isNaN(week)) {
        throw new Error(`Invalid week format: ${timePoint}`);
      }
      const dateInWeek = `MAKE_DATE(${parseInt(year, 10)}, 1, 1) + INTERVAL '1 day' + ((${week} - 1) * INTERVAL '7 days')`;
      boundaryTimestampSQL = `DATE_TRUNC('week', ${dateInWeek})`;
    } else { // day
       if (!/^\d{4}-\d{2}-\d{2}$/.test(timePoint)) {
         throw new Error(`Invalid date format: ${timePoint}`);
       }
      boundaryTimestampSQL = `CAST('${timePoint}' AS DATE)::TIMESTAMP`;
    }
  } catch (error) {
    console.error("Error parsing timePoint:", error);
    // Return an empty query with the expected columns
    return "SELECT NULL AS title, NULL AS color, NULL AS sort, 0 AS count WHERE FALSE;";
  }

  // Query to show activity details for the selected time period, grouped by title + color
  return `
    WITH specific_time_period AS (
      -- Define the single time period we care about
      SELECT ${boundaryTimestampSQL} AS period_start 
    ),
    status_counts AS (
      -- Count statuses with consistent date truncation
      SELECT
        sht.statusId,
        COUNT(DISTINCT sht.subItemId) AS activity_count
      FROM specific_time_period stp
      JOIN filteredStatusHistory sht
        ON DATE_TRUNC('${timeUnit}', sht.validFrom) = stp.period_start
      GROUP BY
        sht.statusId
    ),
    title_color_groups AS (
      -- Join with status templates and group by title/color
      SELECT
        st.title,
        st.color,
        MIN(st.sort) AS sort_order,
        SUM(sc.activity_count) AS activity_count
      FROM status_counts sc
      JOIN status_templates st ON sc.statusId = st.id
      GROUP BY
        st.title,
        st.color
      HAVING SUM(sc.activity_count) > 0
    )
    -- Return the grouped data
    SELECT
      title as title,
      color as color,
      sort_order AS sort,
      activity_count AS count
    FROM title_color_groups
    ORDER BY
      sort_order,
      title;
  `;
};

const fetchTooltipData = async (timePoint) => {
  if (!conn.value) return null;
  
  // Return cached data if available
  if (tooltipCache.value.has(timePoint)) {
    return tooltipCache.value.get(timePoint);
  }
  
  try {
    const tooltipQuery = generateTooltipQuery(timePoint, groupBy.value);
    const data = await executeAndProcessTooltipQuery(tooltipQuery);
    
    // Process and cache the data
    const tooltipData = data.map(item => ({
      title: item.title || 'Unknown',
      color: item.color || '#CCCCCC',
      sort: item.sort,
      count: Number(item.count) || 0
    }));
    
    tooltipCache.value.set(timePoint, tooltipData);
    return tooltipData;
  } catch (error) {
    console.error('Error fetching tooltip data:', error);
    return [];
  }
};

// Debounced tooltip data fetcher
const debouncedFetchTooltipData = (timePoint, params) => {
  clearTimeout(tooltipDebounceTimer.value);
  
  tooltipDebounceTimer.value = setTimeout(async () => {
    if (!tooltipCache.value.has(timePoint)) {
      await fetchTooltipData(timePoint);
      
      // Only refresh tooltip if we're still hovering over the same point
      if (chartRef.value && chartRef.value.chart && currentTooltipParams.value === params) {
        chartRef.value.chart.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: chartData.value.timeData.indexOf(timePoint)
        });
      }
    }
  }, 300); // Reduced debounce time for better responsiveness
};

const fetchActivityData = async () => {
  if (!conn.value) return;
  
  try {
    const dynamicQuery = generateActivityQuery(groupBy.value);
    const result = await executeAndProcessQuery(dynamicQuery);
    
    // Process the data and convert BigInt values to numbers
    const data = result.map(row => ({
      timeKey: row.time_key,
      color: row.color || '#CCCCCC', // Provide a default color if null
      count: Number(row.count),
      sort: Number(row.sort) 
    }));
  
    
    // Process the flat structure
    const timeKeys = [...new Set(data.map(row => row.timeKey))].sort();
    const colorMap = new Map();
    
    // Find all unique colors and create series objects
    data.forEach(row => {
      if (row.color) {
        if (!colorMap.has(row.color)) {
          colorMap.set(row.color, {
            name: row.color,
            type: 'line',
            stack: 'Total',
            smooth: true,
            showSymbol: false,
            emphasis: { focus: 'series' },
            data: new Array(timeKeys.length).fill(0),
            lineStyle: { color: row.color, width: 2 },
            areaStyle: { color: row.color, opacity: 0.6 },
            sort: row.sort
          });
        }
      }
    });
    
    // Fill in activity counts for each time period
    data.forEach(row => {
      if (row.color) {
        const seriesObj = colorMap.get(row.color);
        const timeIndex = timeKeys.indexOf(row.timeKey);
        if (timeIndex !== -1 && seriesObj) {
          seriesObj.data[timeIndex] = row.count;
        }
      }
    });
    
    // Add series names using status templates
    colorMap.forEach((series, color) => {
      const matchingStatuses = Object.values(statusTemplatesObj.value)
        .filter(tpl => tpl.color === color)
        .map(tpl => tpl.title);
      
      if (matchingStatuses.length > 0) {
        series.name = matchingStatuses.join(', ');
      }
    });
    
    // Update chart data
    chartData.value = {
      timeData: timeKeys,
      seriesData: Array.from(colorMap.values()).sort((a, b) => a.sort - b.sort),
      rawTimeKeys: timeKeys
    };
    
    tooltipCache.value = new Map();
    updateChartOption();
    
  } catch (error) {
    console.error('Error fetching activity data:', error);
  }
};

const updateChartOption = () => {
  chartOption.value = {
    tooltip: {
      trigger: 'axis',
      confine: true,
      enterable: true,
      extraCssText: 'max-width: 320px; overflow-x: auto;',
      axisPointer: {
        type: 'shadow',
        label: {
          backgroundColor: '#6a7985'
        }
      },
      formatter: function(params) {
        const timePoint = params[0].axisValue;
        currentTooltipParams.value = params;
        
        // Trigger data fetch with debounce
        if (!tooltipCache.value.has(timePoint)) {
          debouncedFetchTooltipData(timePoint, params);
          
          // Return a simple summary while data is being fetched
          const total = params.reduce((sum, series) => sum + (series.value || 0), 0);
          return `<div style="max-height: 300px; overflow-y: auto;">
                  <strong>${formatAxisLabel(timePoint)}</strong><br/>
                  <strong>${t('total')}: ${total}</strong>
                  </div>`;
        }
        
        // Use cached data if available
        const data = tooltipCache.value.get(timePoint);
        return formatTooltipContent(timePoint, data);
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: chartData.value.timeData,
      axisLabel: {
        color: chartColor.value,
        formatter: formatAxisLabel
      }
    },
    yAxis: {
      type: 'value',
      nameTextStyle: {
        color: chartColor.value
      },
      axisLabel: {
        color: chartColor.value
      }
    },
    dataZoom: [
      {
        type: 'slider',
        show: true,
        xAxisIndex: 0,
        start: zoomStart.value,
        end: zoomEnd.value,
        height: 30,
        bottom: 10,
        borderColor: 'transparent',
        backgroundColor: 'rgba(47, 69, 84, 0.1)',
        dataBackground: {
          lineStyle: {
            color: 'rgba(47, 69, 84, 0.3)'
          },
          areaStyle: {
            color: 'rgba(47, 69, 84, 0.3)'
          }
        },
        fillerColor: 'rgba(167, 183, 204, 0.4)',
        handleStyle: {
          color: '#a7b7cc'
        },
        labelFormatter: (value) => {
          const index = Math.floor(value);
          if (index >= 0 && index < chartData.value.timeData.length) {
            return formatAxisLabel(chartData.value.timeData[index]);
          }
          return '';
        }
      },
      {
        type: 'inside',
        xAxisIndex: 0,
        start: zoomStart.value,
        end: zoomEnd.value,
        zoomOnMouseWheel: 'shift',
        moveOnMouseWheel: true,
        moveOnMouseMove: true
      }
    ],
    series: chartData.value.seriesData.map(series => ({
      ...series,
      stack: 'Total',
      smooth: true,
      showSymbol: false,
      emphasis: {
        focus: 'series'
      }
    }))
  };
};

// Reset zoom to show all data
const resetZoom = () => {
  zoomStart.value = 0;
  zoomEnd.value = 100;
  isZoomed.value = false;
  
  if (chartRef.value && chartRef.value.chart) {
    chartRef.value.chart.dispatchAction({
      type: 'dataZoom',
      start: 0,
      end: 100
    });
  }
};

// Handle chart click (for zoom tracking)
const handleChartClick = () => {
  if (chartRef.value && chartRef.value.chart) {
    const option = chartRef.value.chart.getOption();

    if (option.dataZoom && option.dataZoom[0]) {
      const { start, end } = option.dataZoom[0];
      zoomStart.value = start;
      zoomEnd.value = end;
      isZoomed.value = start !== 0 || end !== 100;
    }
  }
};

const formatTooltipContent = (timePoint, data) => {
  let tooltip = `<div style="max-height: 300px; overflow-y: auto;">
                <strong>${formatAxisLabel(timePoint)}</strong><br/>`;
  
  if (data && data.length > 0) {
    let total = 0;
    
    // Sort items by sort order first, then by title
    const sortedData = [...data].sort((a, b) => {
      if (a.sort !== b.sort) {
        return a.sort - b.sort;
      }
      return a.title.localeCompare(b.title);
    });
    
    // Display each item with its color dot
    sortedData.forEach(item => {
      tooltip += `<span style="color:${item.color}">●</span> ${item.title}: ${item.count}<br/>`;
      total += item.count;
    });
    
    tooltip += `<strong>${t('total')}: ${total}</strong>`;
  } else {
    tooltip += 'No activity in this period';
  }
  
  tooltip += '</div>';
  return tooltip;
};

const formatAxisLabel = (value) => {
  if (groupBy.value === 'week') {
    const [year, week] = value.split('-W');
    return `${t('week')} ${week}, ${year}`;
  } else if (groupBy.value === 'month') {
    const [year, month] = value.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  } else {
    return value;
  }
};

const downloadChart = async () => {
  if (!chartRef.value) return;

  // Get all time periods and apply zoom filter
  const allTimeKeys = chartData.value.timeData || [];
  const totalPoints = allTimeKeys.length;

  // Calculate visible range based on zoom
  let startIdx = Math.floor((zoomStart.value / 100) * totalPoints);
  let endIdx = Math.ceil((zoomEnd.value / 100) * totalPoints);

  // Ensure we have at least one data point when zoomed to the end
  if (startIdx >= endIdx) {
    startIdx = Math.max(0, endIdx - 1);
  }
  endIdx = Math.min(endIdx, totalPoints);

  const visibleTimeKeys = allTimeKeys.slice(startIdx, endIdx);

  // Get series that have non-zero data in the visible range
  const visibleSeries = chartData.value.seriesData.filter(series => {
    if (!series.data) return false;
    for (let i = startIdx; i < endIdx && i < series.data.length; i++) {
      if (series.data[i] > 0) return true;
    }
    return false;
  });

  const visibleColors = new Set(
    visibleSeries
      .map(series => (series.itemStyle?.color || series.lineStyle?.color)?.toLowerCase())
      .filter(Boolean)
  );

  // Query status templates that have activity in the visible time periods
  let colorToTitlesMap = new Map();
  try {
    const timeUnit = groupBy.value === 'month' ? 'month' : (groupBy.value === 'week' ? 'week' : 'day');
    const timeKeySql = timeUnit === 'month'
      ? `strftime(DATE_TRUNC('${timeUnit}', validFrom), '%Y-%m')`
      : timeUnit === 'week'
        ? `strftime(DATE_TRUNC('${timeUnit}', validFrom), '%Y-W%V')`
        : `strftime(DATE_TRUNC('${timeUnit}', validFrom), '%Y-%m-%d')`;

    const timeKeysStr = visibleTimeKeys.map(k => `'${k}'`).join(',');

    if (timeKeysStr) {
      const result = await conn.value.query(`
        SELECT DISTINCT st.color, st.title
        FROM filteredStatusHistory fsh
        JOIN status_templates st ON fsh.statusId = st.id
        WHERE ${timeKeySql} IN (${timeKeysStr})
        ORDER BY st.sort
      `);
      const templates = result.toArray().map(row => row.toJSON());

      templates.forEach(tpl => {
        const color = (tpl.Color || tpl.color)?.toLowerCase();
        const title = tpl.Title || tpl.title;
        if (color && title && visibleColors.has(color)) {
          if (!colorToTitlesMap.has(color)) {
            colorToTitlesMap.set(color, new Set());
          }
          colorToTitlesMap.get(color).add(title);
        }
      });
    }
  } catch (error) {
    console.error('Error fetching status templates:', error);
  }

  // Build legend data for visible series
  const seriesDataForLegend = visibleSeries.map(series => {
    const color = series.itemStyle?.color || series.lineStyle?.color;
    const colorLower = color?.toLowerCase();

    const matchingTitlesSet = colorToTitlesMap.get(colorLower);
    const matchingTitles = matchingTitlesSet ? [...matchingTitlesSet] : [];
    const displayName = matchingTitles.length > 0 ? matchingTitles.join(', ') : series.name;

    return {
      name: displayName,
      titles: [displayName],
      color: color
    };
  });

  downloadChartAsPNG(chartRef.value.chart, `${t('rdriveVelocityChart')}.png`, seriesDataForLegend);
};

watch([zoomStart, zoomEnd], ([newStart, newEnd]) => {
  isZoomed.value = newStart !== 0 || newEnd !== 100;
  
  if (chartRef.value && chartRef.value.chart) {
    chartRef.value.chart.setOption({
      dataZoom: [
        { start: newStart, end: newEnd },
        { start: newStart, end: newEnd }
      ]
    });
  }
});

watch(
  [
    () => props.filterContext.filtersLastUpdated,
    groupBy
  ],
  () => {
    // Clear tooltip cache when relevant data changes
    tooltipCache.value = new Map();
    // Reset zoom when data changes
    zoomStart.value = 0;
    zoomEnd.value = 100;
    isZoomed.value = false;
    fetchActivityData();
  },
  { deep: true }
);

watch(() => chartColor.value, () => {
  updateChartOption();
});

watch(() => props.dataLoaded, () => {
  fetchActivityData();
});

onMounted(() => {
  //fetchActivityData();
  
  // Set up zoom change handler
  if (chartRef.value && chartRef.value.chart) {
    chartRef.value.chart.on('datazoom', (params) => {
      zoomStart.value = params.start;
      zoomEnd.value = params.end;
      isZoomed.value = params.start !== 0 || params.end !== 100;
    });
  }
});
</script>

<style scoped>
.chart {
  height: 400px;
}
</style>